import { Injectable } from '@angular/core';
import { Observable, of, throwError, zip } from 'rxjs';
import { fromPromise } from 'rxjs/internal-compatibility';
import { catchError, map, mergeMap, shareReplay, switchMap, tap } from 'rxjs/operators';
import * as CidaasSDK from 'cidaas-javascript-sdk';
import { IInitiateMFAPayload, IMFAListPayload, IMFAListResponse } from '../model/mfa.interface';
import {
  firstTimePwdReset,
  IHandleResetPwdPayload,
  ResetPasswordRequest,
  resetPwdPayload,
} from '../model/reset-password-request.interface';
import { ISDKConfig } from '../model/sdk-config.interface';
import { IRequestBody } from '../model/request-body.interface';
import { SharedDataService } from './shared-data.service';
import { IClientInfo, IGroupClientInfoResponse } from '../model/client-info.interface';
import { isResponseType } from './service.model';
import { TenantInfoApi } from '../model/tenant-info.interface';
import { mapCidaasResponseToData } from './cidaas-api.service';
import { Params, Router } from '@angular/router';
import { CheckUserRequest, generateUserCheckRequest } from '../model/domain/user.model';
import { CacheService } from './cache.service';
import { mapToUserCheckResult, UserCheckAPIRequest, UserCheckResult, UserCheckResultV1 } from '../model/login.interface';
import { ResponseBody } from '../model/api.model';
import { IPreloginMetadataPayload, IPreloginMetadataResponse } from '../model/suggest-verification-method.interface';

const CACHE_SIZE = 1;
const KeyClientInfo = 'client_info';

@Injectable({
  providedIn: 'root',
})
export class CidaasService {
  private tenantCache$?: Observable<any>;
  private scopeCache$?: Observable<any>;

  // variable declaration
  private cidaas_sdk: any;

  //  constructor
  constructor(
    private _dataSerivce: SharedDataService,
    private _router: Router,
    private cacheService: CacheService) {

   }

  // property methods

  // initialising cidaas sdk
  _initSDK = (options: Pick<ISDKConfig, 'authority'>): void => {
    this.cidaas_sdk = new CidaasSDK.WebAuth(options);
  };

  _get(): any {
    return this.cidaas_sdk;
  }

  // get client info
  _getClientInfo = async (options: Pick<IRequestBody, 'requestId'>) => {
    const response = await this.cidaas_sdk.getClientInfo(options);
    if (!response.success && response.status == 417) {
      const queryParams: Params = { error_code: response.code, error_hint: response.error, error_description: response.error };
      this.navigateToErrorPage(queryParams);
    }
    this._dataSerivce._setClientInfo(response.data);
  };

  // get device info
  _getDeviceInfo = async () => {
    await this.cidaas_sdk.getDeviceInfo();
  };

  _getClientInfoWithTenant = (options: { requestId?: string }): Observable<[IClientInfo, TenantInfoApi]> => {
    const requestId = options.requestId || '';
    const getClientInfo$ = requestId && this._getClientInfoAsObs({ requestId }) || of({});
    return zip(
      getClientInfo$.pipe(
        catchError(() => {
          return of({});
        })
    ), this.getTenantInfo()) ;
  }
  // get client info
  _getClientInfoAsObs = (options: Pick<IRequestBody, 'requestId'>): Observable<IClientInfo> => {
    // TODO:  temporary fix applied, needs to be removed later
    if (!options.requestId){
      return of({});
    }
    return fromPromise(this.cidaas_sdk.getClientInfo(options))
      .pipe(
        map(response => {
          if (isResponseType<IClientInfo>(response)){
            return response.data;
          }
          throw new Error(`Invalid client info: ${JSON.stringify(response)}`);
        }),
        tap(clientInfo => {
          if (clientInfo) {
            this._dataSerivce._setClientInfo(clientInfo);
          }
        })
      );
  }

  getClientInfo$ = (requestId: string): Observable<IClientInfo> => {
    return this.cacheService.getCachedObservable({
      key: KeyClientInfo,
      trackKey: requestId
    }, () => this._getClientInfoAsObs({ requestId }));
  }

  // get user exist
  _getUserExists = async (options: UserCheckAPIRequest) => {
    const response = await this.cidaas_sdk.userCheckExists(options);
    return response ? response : false;
  };

 // get user exists - usersactions-srv
  getUserExistsInfoFromApi = async (options: UserCheckAPIRequest): Promise<ResponseBody<UserCheckResultV1>> => {
    if (!options.webfinger) {
      throw new Error(`webfinger cannot be undefined: ${JSON.stringify(options)}`);
    }

    return await this.cidaas_sdk.userCheckExistsV2(options) as
      ResponseBody<UserCheckResultV1>;
  }

  getUserExistsInfoFromApi$ = (options: UserCheckAPIRequest): Observable<UserCheckResult> => {
    return mapCidaasResponseToData<UserCheckResultV1>(this.getUserExistsInfoFromApi(options))
      .pipe(
        map((result) => mapToUserCheckResult(result))
      );
  }

  getUserExistenceForRequestId$ = (options: CheckUserRequest): Observable<UserCheckResult> => {
    const reqData = generateUserCheckRequest(options);
    return this.getClientInfo$(reqData.requestId).pipe(
           switchMap((client) => {
             const userCheckRequest: UserCheckAPIRequest = {
               webfinger: client.webfinger || 'no_redirection',
               rememberMe: client.is_remember_me_selected || false,
               ...reqData
             };
             return this.getUserExistsInfoFromApi$(userCheckRequest);
           })
    );
  }


  // social login
  _socialLogin = async (options: Pick<IRequestBody, 'requestId' | 'provider'>) => {
    await this.cidaas_sdk.loginWithSocial(options);
  };

  // get mfa list
  _getMFAList = async (options: Partial<IRequestBody>) => {
    return await this.cidaas_sdk.getMFAListV2(options);
  };

  _checkStatusIDStatus = async (statusId: string) => {
    const response = await this.cidaas_sdk.updateSocket(statusId);
    return response?.data;
  };

  _loginWithCredentials = (options): void => {
    this.cidaas_sdk.loginWithCredentials(options);
  };

  _passwordlessLogin = async (options): Promise<void> => {
    await this.cidaas_sdk.passwordlessLogin(options);
  };

  // get list of mfas configured
  getMFAList(data: IMFAListPayload): Promise<IMFAListResponse> {
    return new Promise((resolve, reject) => {
      this.cidaas_sdk
        .getMFAListV2(data)
        .then((response) => {
          resolve(response);
        })
        .catch((ex) => {
          reject(ex);
        });
    });
  }

  initiateMFAV2(data: IInitiateMFAPayload): Promise<any> {
    return new Promise((resolve, reject) => {
      this.cidaas_sdk
        .initiateMFAV2(data)
        .then((response) => {
          resolve(response);
        })
        .catch((ex) => {
          reject(ex);
        });
    });
  }

  getInviteUserDetails(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.cidaas_sdk
        .getInviteUserDetails(data)
        .then((response) => {
          resolve(response);
        })
        .catch((ex) => {
          reject(ex);
        });
    });
  }

  authenticateMFAV2(data: any): any {
    return new Promise((resolve, reject) => {
      this.cidaas_sdk
        .authenticateMFAV2(data)
        .then((response) => {
          resolve(response);
        })
        .catch((ex) => {
          reject(ex);
        });
    });
  }

  cancelMFAV2(data: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.cidaas_sdk
        .cancelMFAV2(data)
        .then((response) => {
          resolve(response);
        })
        .catch((ex) => {
          reject(ex);
        });
    });
  }

  getMFAStatus(statusId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.cidaas_sdk
        .updateSocket(statusId)
        .then((response) => {
          resolve(response);
        })
        .catch((ex) => {
          reject(ex);
        });
    });
  }

  mfaContinue(data: any): any {
    this.cidaas_sdk.mfaContinue(data);
    // this._spinner._stop();
  }

  firstTimeChangePassword(data: firstTimePwdReset): void {
    this.cidaas_sdk.firstTimeChangePassword(data);
    // this._spinner._stop();
  }

  initiatePasswordReset(data: ResetPasswordRequest): Promise<any> {
    return new Promise((resolve, reject) => {
      this.cidaas_sdk
        .initiateResetPassword(data)
        .then((response) => {
          resolve(response);
        })
        .catch((ex) => {
          reject(ex);
        });
    });
  }

  handleResetPassword(data: IHandleResetPwdPayload): void {
    this.cidaas_sdk.handleResetPassword(data);
    // this._spinner._stop();
  }

  resetPassword(data: resetPwdPayload): void {
    this.cidaas_sdk.resetPassword(data);
  }

  _getRegistrationSetup = async (options) => {
    return new Promise((resolve, reject) => {
      this.cidaas_sdk
        .getRegistrationSetup(options)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          console.log(error);
        });
    });
  };

  _getMissingFieds$ = (viewType:string, options:IPreloginMetadataPayload): Observable<IPreloginMetadataResponse> => {
    if (viewType != undefined && viewType.toLowerCase() === 'login') {
    return fromPromise(this.cidaas_sdk.getMissingFieldsLogin(options.trackId)).pipe(
      mergeMap((res:any)=>!res.success || !res.data ? throwError({message: `Invalid prelogin metadata: ${JSON.stringify(res)}`, status: res.status, code: res.code }): of(res.data))
    )
  } else{
    return fromPromise(this.cidaas_sdk.getMissingFields(options)).pipe(
      map((response) => {
        if (response && isResponseType<IPreloginMetadataResponse>(response)) {
          return response.data;
          // Promise.resolve(response.data)
        }
        throw new Error(`Invalid prelogin metadata: ${JSON.stringify(response)}`);
      })
    )
  }
  }

  _getMissingFields = async (viewType: string, options: any) => {
    if (viewType != undefined && viewType.toLowerCase() === 'login') {
      return new Promise((resolve, reject) => {
        this.cidaas_sdk
          .getMissingFieldsLogin(options.trackId)
          .then((response) => {
            if (response && response.data) resolve(response.data);
            else throw new Error(`Whoops! prelogin/metadata returns ${response}`);
          })
          .catch((error) => {
            console.log(error);
          });
      });
    } else {
      return new Promise((resolve, reject) => {
        this.cidaas_sdk
          .getMissingFields(options)
          .then((response) => {
            resolve(response.data);
          })
          .catch((error) => {
            console.log(error);
          });
      });
    }
  };

  _register = async (options, headers): Promise<any> => {
    return new Promise((resolve, reject) => {
      this.cidaas_sdk
        .register(options, headers)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          reject(error);
          console.log(error);
        });
    });
  };

  _progressiveRegister = async (options, headers) => {
    return new Promise((resolve, reject) => {
      this.cidaas_sdk
        .progressiveRegistration(options, headers)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          console.log(error);
        });
    });
  };

  _loginAfterRegister = async (options) => {
    return new Promise((resolve, reject) => {
      try {
        this.cidaas_sdk.loginAfterRegister(options);
        resolve(true);
      } catch (e) {
        reject(e);
      }
    });
  };

  _deviceCodeVerify = (userCode): void => {
    this.cidaas_sdk.deviceCodeVerify(userCode);
  };

  initiateAccountVerification(data: any): void {
    this.cidaas_sdk.initiateAccountVerification(data);
  }

  getCommunicationStatus(data: Pick<IRequestBody, 'sub' | 'provider' | 'requestId'>): Promise<any> {
    return new Promise((resolve, reject) => {
      this.cidaas_sdk
        .getCommunicationStatus(data)
        .then((response) => {
          resolve(response);
        })
        .catch((ex) => {
          reject(ex);
        });
    });
  }

  getTenantInfo(): Observable<TenantInfoApi> {
    if (!this.tenantCache$) {
      this.tenantCache$ = this.getTenantInfoFromApi().pipe(shareReplay(CACHE_SIZE));
    }
    return this.tenantCache$;
  }

  getTenantInfoFromApi(): Observable<TenantInfoApi> {
    return mapCidaasResponseToData(this.cidaas_sdk.getTenantInfo());
  }

  getScopeDetails(options): Observable<IGroupClientInfoResponse> {
    return this.getScopeDetailsFromApi(options);
  }

  getScopeDetailsFromApi(options): Observable<IGroupClientInfoResponse> {
    return mapCidaasResponseToData(this.cidaas_sdk.getScopeConsentDetails(options));
  }



  navigateToErrorPage = (params: Params) => {
    const queryParams = {
      ...params,
      ...((!params.error_hint && { error_hint: 'ERROR.MSG' }) || {}),
    };
    this._router.navigate(['handle_error'], {
      queryParams,
    });
  };
}
